import React, { useEffect, useMemo, useState } from 'react';
import { useRouter } from 'next/router';
import styles from './DMCA.module.css';
import Accordion from '../components/Accordions/Accordion';
import sectionStyles from '../components/Sections/Section.module.css';

const emptyForm = {
  reportType: 'rights_concern',
  mediaId: '',
  reporterName: '',
  email: '',
  phone: '',
  address: '',
  socialMediaAccount: '',
  copyrightedWorkDescription: '',
  originalContentLink: '',
  explanation: '',
  signature: '',
  goodFaithStatement: false,
  accuracyStatement: false,
  authorityStatement: false,
};

export default function DMCA() {
  const router = useRouter();
  const { contentHash, mediaId: mediaIdQuery } = router.query;
  const [formData, setFormData] = useState(emptyForm);
  const [mediaOptions, setMediaOptions] = useState([]);
  const [loadingMedia, setLoadingMedia] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [caseId, setCaseId] = useState('');
  const [error, setError] = useState('');

  useEffect(() => {
    if (!router.isReady) return;
    if (!contentHash) {
      setError('Invalid access. Open this form from the content item containing the media.');
      setLoadingMedia(false);
      return;
    }
    let cancelled = false;
    setLoadingMedia(true);
    fetch(`/api/contentDisplay/${encodeURIComponent(contentHash)}`)
      .then((response) => response.ok ? response.json() : Promise.reject(new Error('Content not found.')))
      .then((data) => fetch(`/api/content/${data.content.content_id}/media`).then((response) => response.json()))
      .then((data) => {
        if (cancelled) return;
        const options = (data.galleries || []).flatMap((gallery) => gallery.items.map((item) => ({ ...item, galleryTitle: gallery.title })));
        const unique = [...new Map(options.map((item) => [item.mediaId, item])).values()];
        setMediaOptions(unique);
        setFormData((current) => ({ ...current, mediaId: unique.some((item) => item.mediaId === mediaIdQuery) ? mediaIdQuery : current.mediaId || unique[0]?.mediaId || '' }));
      })
      .catch((loadError) => { if (!cancelled) setError(loadError.message); })
      .finally(() => { if (!cancelled) setLoadingMedia(false); });
    return () => { cancelled = true; };
  }, [contentHash, mediaIdQuery, router.isReady]);

  const selectedMedia = useMemo(() => mediaOptions.find((item) => item.mediaId === formData.mediaId), [formData.mediaId, mediaOptions]);
  const formal = formData.reportType === 'formal_dmca';
  const handleChange = (event) => {
    const { name, type, checked, value } = event.target;
    setFormData((current) => ({ ...current, [name]: type === 'checkbox' ? checked : value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    const response = await fetch('/api/dmca', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        contentHash,
        ...formData,
        reportedMaterialUrl: selectedMedia?.url ? new URL(selectedMedia.url, window.location.origin).toString() : window.location.href,
      }),
    });
    const data = await response.json();
    if (!response.ok) return setError(data.message || 'The report could not be submitted.');
    setCaseId(data.caseId || '');
    setSubmitted(true);
  };

  const form = (
    <div className={styles.container}>
      {submitted ? (
        <>
          <h1 className={styles.title}>Rights Report Received</h1>
          <p>Thank you. The identified media has been placed into the appropriate review workflow.</p>
          {caseId && <p>Case reference: <strong>{caseId}</strong></p>}
        </>
      ) : (
        <form onSubmit={handleSubmit} className={styles.form}>
          <label className={styles.formLabel} htmlFor="reportType">Type of report</label>
          <select id="reportType" name="reportType" value={formData.reportType} onChange={handleChange} className={styles.formInput}>
            <option value="rights_concern">Rights concern</option>
            <option value="formal_dmca">Formal DMCA notice</option>
          </select>

          <label className={styles.formLabel} htmlFor="mediaId">Media being reported</label>
          <select id="mediaId" name="mediaId" value={formData.mediaId} onChange={handleChange} className={styles.formInput} required disabled={loadingMedia || !mediaOptions.length}>
            {loadingMedia && <option>Loading media…</option>}
            {!loadingMedia && !mediaOptions.length && <option value="">No reportable media found</option>}
            {mediaOptions.map((item) => <option key={item.mediaId} value={item.mediaId}>{item.galleryTitle} — {item.title || item.creator || item.url}</option>)}
          </select>
          {selectedMedia && <p><a href={selectedMedia.url} target="_blank" rel="noopener noreferrer">Open the selected media</a></p>}

          <label className={styles.formLabel} htmlFor="reporterName">Your name {formal && '(required)'}</label>
          <input className={styles.formInput} id="reporterName" name="reporterName" value={formData.reporterName} onChange={handleChange} required={formal} />
          <label className={styles.formLabel} htmlFor="email">Email address</label>
          <input className={styles.formInput} type="email" id="email" name="email" value={formData.email} onChange={handleChange} required />
          <label className={styles.formLabel} htmlFor="socialMediaAccount">Creator account or profile</label>
          <input className={styles.formInput} id="socialMediaAccount" name="socialMediaAccount" value={formData.socialMediaAccount} onChange={handleChange} />
          <label className={styles.formLabel} htmlFor="originalContentLink">Link to the original work {formal && '(required)'}</label>
          <input className={styles.formInput} type="url" id="originalContentLink" name="originalContentLink" value={formData.originalContentLink} onChange={handleChange} required={formal} />
          <label className={styles.formLabel} htmlFor="explanation">Explain the concern</label>
          <textarea className={styles.formInput} id="explanation" name="explanation" value={formData.explanation} onChange={handleChange} rows={5} />

          {formal && <>
            <label className={styles.formLabel} htmlFor="copyrightedWorkDescription">Identify the copyrighted work</label>
            <textarea className={styles.formInput} id="copyrightedWorkDescription" name="copyrightedWorkDescription" value={formData.copyrightedWorkDescription} onChange={handleChange} rows={4} required />
            <label className={styles.formLabel} htmlFor="phone">Telephone number</label>
            <input className={styles.formInput} id="phone" name="phone" value={formData.phone} onChange={handleChange} />
            <label className={styles.formLabel} htmlFor="address">Address</label>
            <textarea className={styles.formInput} id="address" name="address" value={formData.address} onChange={handleChange} rows={3} required />
            <label><input type="checkbox" name="goodFaithStatement" checked={formData.goodFaithStatement} onChange={handleChange} required /> I have a good-faith belief that the reported use is not authorized by the copyright owner, its agent, or the law.</label>
            <label><input type="checkbox" name="accuracyStatement" checked={formData.accuracyStatement} onChange={handleChange} required /> The information in this notice is accurate and, under penalty of perjury, I am authorized to act for the copyright owner.</label>
            <label><input type="checkbox" name="authorityStatement" checked={formData.authorityStatement} onChange={handleChange} required /> I understand this is a formal legal notice and consent to receiving case communications.</label>
            <label className={styles.formLabel} htmlFor="signature">Electronic signature</label>
            <input className={styles.formInput} id="signature" name="signature" value={formData.signature} onChange={handleChange} required />
          </>}
          {error && <p className={styles.error}>{error}</p>}
          <button type="submit" className={styles.submitButton} disabled={loadingMedia || !formData.mediaId}>Submit report</button>
        </form>
      )}
    </div>
  );

  return (
    <section className={sectionStyles.section}>
      <div className={styles.topPane}>
        <h2 className={styles.descriptionTitle}>Report a Rights Concern</h2>
        <p className={styles.description}>Identify the exact image or video at issue. A general concern enters the review queue; a complete formal notice disables public access to the identified media while it is reviewed.</p>
      </div>
      <Accordion items={[{ header: submitted ? 'Submission received' : 'Media rights report', content: form }]} />
    </section>
  );
}
